import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Car, CheckCircle2, Loader2, Bike } from "lucide-react";
import PhotoPickerField from "@/components/price/PhotoPickerField";
import { PriceScooterIcon } from "@/components/price/PriceIllustrations";
import { useAuth } from "@/context/AuthContext";

/**
 * BecomeDriver — Un passager (déjà connecté) active son profil
 * conducteur en complétant les infos véhicule.
 *
 * Formulaire minimaliste : brand/model, plaque, type de véhicule,
 * photo optionnelle, code créateur optionnel. On ne redemande ni
 * email ni mot de passe (le user est déjà authentifié).
 *
 * Après succès, redirige vers /dashboard/driver (active_mode est
 * basculé côté serveur par /price/me/become-driver).
 */
export default function BecomeDriver() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    vehicle_brand_model: "",
    license_plate: "",
    price_vehicle_kind: "car",
  });
  const [photoUrl, setPhotoUrl] = useState("");
  const [creatorCode, setCreatorCode] = useState("");
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [creatorError, setCreatorError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Validation live du code créateur (si saisi)
  useEffect(() => {
    const code = creatorCode.trim().toUpperCase();
    if (!code) { setCreatorInfo(null); setCreatorError(""); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/price/creator-codes/validate?code=${encodeURIComponent(code)}`);
        setCreatorInfo(data); setCreatorError("");
      } catch (err) {
        setCreatorInfo(null);
        setCreatorError(err?.response?.status === 404 ? "Code inconnu ou désactivé" : "");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [creatorCode]);

  const upd = (k) => (e) => {
    const v = k === "license_plate" ? e.target.value.toUpperCase() : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const vehicle = form.vehicle_brand_model.trim();
    const plate = form.license_plate.trim();
    if (!vehicle) return toast.error("Marque et modèle obligatoires");
    if (!plate) return toast.error("Plaque obligatoire");

    setLoading(true);
    try {
      await api.post("/price/me/become-driver", {
        vehicle_brand_model: vehicle,
        license_plate: plate,
        price_vehicle_kind: form.price_vehicle_kind,
        photo_url: photoUrl || undefined,
        creator_code: creatorCode.trim().toUpperCase() || undefined,
      });
      try { await refresh?.(); } catch { /* best-effort */ }
      setDone(true);
      // Petite pause visuelle avant redirection
      setTimeout(() => nav("/verify-identity?signup=1", { replace: true }), 1200);
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de créer ton profil conducteur"));
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="relative min-h-screen text-foreground street-dots" style={{ background: "hsl(15 100% 96%)" }}>
        <div
          className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center px-6"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="w-full rounded-3xl bg-white p-8 street-border street-shadow text-center">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-accent rotate-[-4deg] street-border street-shadow-sm">
              <CheckCircle2 className="h-10 w-10 text-accent-foreground" strokeWidth={2.5} />
            </div>
            <h1 className="mt-5 font-[Outfit] text-[26px] font-black leading-tight tracking-tight">
              Profil conducteur créé !
            </h1>
            <p className="mt-3 text-sm text-foreground/70 font-medium">
              On t&apos;emmène sur ton dashboard conducteur…
            </p>
            <Loader2 className="mt-4 h-5 w-5 animate-spin mx-auto text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen text-foreground street-dots" style={{ background: "hsl(15 100% 96%)" }} data-testid="become-driver-page">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.42), transparent 70%)" }}
      />
      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <Link
          to="/dashboard/client"
          className="street-press mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-foreground street-border-thin street-shadow-sm"
          data-testid="become-driver-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Retour
        </Link>

        <header className="mt-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-accent rotate-[-4deg] street-border street-shadow-sm">
            <Car className="h-8 w-8 text-accent-foreground" strokeWidth={2.5} />
          </div>
          <h1 className="mt-5 font-[Outfit] text-[32px] font-black leading-[1.02] tracking-tight text-foreground">
            Deviens aussi
            <span className="ml-1 inline-block rounded-xl bg-primary px-2 py-0.5 street-border-thin rotate-[-2deg] text-primary-foreground">
              covoitureur
            </span>
          </h1>
          <p className="mt-3 text-sm text-foreground/70 font-medium">
            On garde ton compte passager intact. Tu bascules entre les
            2 modes depuis ton profil. Juste quelques infos sur ton véhicule.
          </p>
        </header>

        <form onSubmit={submit} className="mt-8 space-y-4">
          {/* Type de véhicule */}
          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2 block">
              Type de véhicule
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "car", label: "Voiture", icon: Car },
                { v: "motorcycle", label: "Moto", icon: Bike },
                { v: "scooter", label: "Scooter", icon: PriceScooterIcon },
              ].map(({ v, label, icon: Icon }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, price_vehicle_kind: v }))}
                  data-testid={`become-driver-kind-${v}`}
                  className={`street-press rounded-2xl p-3 flex flex-col items-center gap-1.5 street-border-thin ${form.price_vehicle_kind === v ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}
                >
                  <Icon className="h-6 w-6" strokeWidth={2.5} />
                  <span className="text-[11px] font-black uppercase tracking-wider">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <Field
            label="Marque et modèle"
            value={form.vehicle_brand_model}
            onChange={upd("vehicle_brand_model")}
            placeholder="Renault Clio"
            testid="become-driver-vehicle"
          />
          <Field
            label="Plaque d'immatriculation"
            value={form.license_plate}
            onChange={upd("license_plate")}
            placeholder="AB-123-CD"
            testid="become-driver-plate"
          />

          {/* Photo optionnelle */}
          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2 block">
              Photo du véhicule (optionnel)
            </label>
            <PhotoPickerField
              value={photoUrl}
              onChange={setPhotoUrl}
              testid="become-driver-photo"
            />
          </div>

          {/* Code créateur optionnel */}
          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-1.5 block">
              Code créateur (optionnel)
            </label>
            <input
              value={creatorCode}
              onChange={(e) => setCreatorCode(e.target.value.toUpperCase())}
              placeholder="JEANMARC"
              data-testid="become-driver-creator-code"
              className="w-full rounded-xl bg-white px-3 py-2.5 font-[Outfit] font-bold text-foreground placeholder:text-foreground/35 street-border-thin street-shadow-sm outline-none"
              style={{ fontSize: "16px" }}
            />
            {creatorInfo && (
              <p className="mt-1 text-[11px] font-medium text-emerald-700">
                ✅ Code valide — {creatorInfo.label || creatorInfo.name || "créateur reconnu"}
              </p>
            )}
            {creatorError && (
              <p className="mt-1 text-[11px] font-medium text-rose-700">{creatorError}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="become-driver-submit"
            className="street-press mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black tracking-tight text-primary-foreground street-border street-shadow disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Création en cours…
              </>
            ) : (
              <>Activer mon profil conducteur</>
            )}
          </button>
          <p className="text-[11px] text-foreground/55 font-medium text-center">
            Ensuite tu complèteras ton KYC et Stripe depuis le dashboard
            conducteur pour pouvoir publier des trajets.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, autoComplete }) {
  return (
    <div>
      <label className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-1.5 block">
        {label}
      </label>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full rounded-xl bg-white px-3 py-2.5 font-[Outfit] font-bold text-foreground placeholder:text-foreground/35 street-border-thin street-shadow-sm outline-none"
        style={{ fontSize: "16px" }}
      />
    </div>
  );
}
